import { useEffect, useRef, useState } from "react";
import { geoStats, type GeoStats } from "../lib/geo";
import type { Position } from "../lib/geo";
import type { SpeedLimit } from "../lib/road";

type Props = {
  /** Snapped / displayed position (post road-matching). */
  position: Position | null;
  limit: SpeedLimit;
  roadError: string | null;
  onClose: () => void;
};

const KMH = (ms: number | null | undefined) =>
  ms == null ? "—" : `${(ms * 3.6).toFixed(1)} km/h`;
const DEG = (d: number | null | undefined) =>
  d == null ? "—" : `${d.toFixed(0)}°`;

export function DebugPanel({ position, limit, roadError, onClose }: Props) {
  // Poll the mutable stats object a few times a second — cheap, and
  // avoids re-rendering the whole app on the 60 Hz position path.
  const [, setTick] = useState(0);
  const snap = useRef<GeoStats>(geoStats);
  useEffect(() => {
    const id = window.setInterval(() => { snap.current = geoStats; setTick((t) => t + 1); }, 250);
    return () => clearInterval(id);
  }, []);

  const s = snap.current;
  const ageMs = s.lastFixTs ? Date.now() - s.lastFixTs : null;
  const ageStale = ageMs != null && ageMs > (s.avgFixIntervalMs || 1500) * 1.5;

  return (
    <div className="debug-panel">
      <div className="debug-head">
        <span>GPS DEBUG</span>
        <button className="debug-close" onClick={onClose} aria-label="Luk debug">×</button>
      </div>

      <Section title="Source">
        <Row k="Mode" v={s.source.toUpperCase()} tone={s.source === "gps" ? "good" : "warn"} />
        <Row k="Fixes" v={String(s.fixCount)} />
        <Row
          k="Last fix age"
          v={ageMs == null ? "—" : `${(ageMs / 1000).toFixed(1)} s`}
          tone={ageStale ? "bad" : "good"}
        />
        <Row
          k="Fix interval"
          v={s.fixIntervalMs ? `${(s.fixIntervalMs / 1000).toFixed(2)} s` : "—"}
          tone={s.fixIntervalMs > 2500 ? "bad" : s.fixIntervalMs > 1500 ? "warn" : "good"}
        />
        <Row k="Avg interval" v={s.avgFixIntervalMs ? `${(s.avgFixIntervalMs / 1000).toFixed(2)} s` : "—"} />
        <Row
          k="Rate"
          v={s.avgFixIntervalMs ? `${(1000 / s.avgFixIntervalMs).toFixed(2)} Hz` : "—"}
        />
        <Row k="Accuracy" v={s.accuracyM == null ? "—" : `${s.accuracyM.toFixed(0)} m`} />
      </Section>

      <Section title="Device reports">
        <Row k="Speed" v={KMH(s.rawSpeedMs)} tone={s.rawSpeedMs == null ? "warn" : undefined} />
        <Row k="Heading" v={DEG(s.rawHeading)} tone={s.rawHeading == null ? "warn" : undefined} />
      </Section>

      <Section title="Derived (fix-diff)">
        <Row k="Speed" v={KMH(s.derivedSpeedMs)} />
        <Row k="Heading" v={DEG(s.derivedHeading)} />
      </Section>

      <Section title="Used → dead-reckon">
        <Row k="Speed" v={KMH(s.usedSpeedMs)} />
        <Row k="Heading" v={DEG(s.usedHeading)} />
        <Row
          k="Predicting"
          v={s.deadReckon ? "YES" : "no"}
          tone={s.deadReckon ? "good" : "warn"}
        />
        <Row k="Project ahead" v={`${s.predictAheadM.toFixed(0)} m`} />
      </Section>

      <Section title="Position">
        <Row k="Raw lat" v={s.lat == null ? "—" : s.lat.toFixed(6)} />
        <Row k="Raw lon" v={s.lon == null ? "—" : s.lon.toFixed(6)} />
        <Row k="Snapped" v={position ? `${position.lat.toFixed(6)}, ${position.lon.toFixed(6)}` : "—"} />
        <Row k="Road" v={limit.name || limit.highway || "—"} />
        <Row k="Limit" v={limit.kmh == null ? "—" : `${limit.kmh} km/h`} />
        {roadError && <Row k="API Error" v={roadError} tone="bad" />}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="debug-section">
      <div className="debug-section-title">{title}</div>
      {children}
    </div>
  );
}

function Row({ k, v, tone }: { k: string; v: string; tone?: "good" | "warn" | "bad" }) {
  return (
    <div className="debug-row">
      <span className="debug-k">{k}</span>
      <span className={`debug-v${tone ? ` debug-${tone}` : ""}`}>{v}</span>
    </div>
  );
}
